import BackendService from 'BackendService';
import InvestorContext from 'InvestorContext';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FundOpportunityEOI from '../../components/FundOpportunityEOI';
import FundOpportunity from '../../models/FundOpportunity';
import Investor from '../../models/Investor';
import { CommonLoggedInDataContext, useCommonLoggedInData } from 'CommonLoggedInData';
import LoadingSpinner from './LoadingSpinner';
import LookupOpportunityError from './LookupOpportunityError';
import { FundUpdate } from './nonAdminLazyImports';
import {
  findInvestorForFundOpportunityFallback,
  resolveInvestorForFundOpportunity,
} from './nonAdminAuthUtils';

export interface LookupOpportunityProps {
  type: 'FundOpportunity' | 'FundLead' | 'FundUpdate';
  print?: boolean;
  printDraft?: boolean;
}

export default function LookupOpportunity({ type, print, printDraft }: LookupOpportunityProps) {
  const { id } = useParams();
  const [fundOpportunity, setFundOpportunity] = useState({
    data: null as FundOpportunity | null,
    investor: null as Investor | null,
    content: null as null | string,
  });
  const [investor, setInvestor] = useState<Investor | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loggedInData = useCommonLoggedInData();
  const investorContext = useContext(InvestorContext);
  const { investors, loading } = investorContext;

  useEffect(() => {
    let cancelled = false;
    setError(null);

    const lookup = resolveInvestorForFundOpportunity(investors, id, loading());

    if (!lookup.stillLoading) {
      if (lookup.investor) {
        setInvestor(lookup.investor);
        setFundOpportunity({
          data: lookup.investor.fundOpportunity!,
          investor: lookup.investor,
          content: null,
        });
      } else if (lookup.error === 'Fund opportunity not found or not accessible.' && id) {
        // Not in the cached (COMPLETE-excluding) list — a closed fund isn't
        // in it by default. Fall back to a targeted fetch, same as the
        // native app; the backend returns it regardless of status when
        // fundOpportunityId is supplied.
        findInvestorForFundOpportunityFallback(new BackendService(), id)
          .then((fallbackInvestor) => {
            if (cancelled) return;
            if (fallbackInvestor) {
              setInvestor(fallbackInvestor);
              setFundOpportunity({
                data: fallbackInvestor.fundOpportunity!,
                investor: fallbackInvestor,
                content: null,
              });
            } else {
              console.warn(`No investor found with fund opportunity ID: ${id}`);
              setError(lookup.error);
            }
          })
          .catch(() => {
            if (!cancelled) {
              setError(lookup.error);
            }
          });
      } else if (lookup.error) {
        if (lookup.error === 'No fund opportunities available.') {
          console.warn('No investors available for user');
        }
        setError(lookup.error);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [id, type, investors, loading]);

  if (loading()) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <LookupOpportunityError message={error} />;
  }

  if (!fundOpportunity.data) {
    return <LoadingSpinner />;
  }

  if (type == 'FundUpdate') {
    if (!investor) {
      return <LoadingSpinner />;
    }

    return <FundUpdate fundOpportunity={fundOpportunity.data} investor={investor} />;
  }

  const key = `${fundOpportunity.investor!.id}-${fundOpportunity.investor!.updatedAt}`;

  return (
    <CommonLoggedInDataContext.Provider value={loggedInData}>
      <FundOpportunityEOI
        print={print}
        printDraft={!!printDraft}
        investorId={fundOpportunity.investor!.id}
        key={key}
      />
    </CommonLoggedInDataContext.Provider>
  );
}
